import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Progress } from "../../components/ui/progress";
import { 
  Building2, FileText, Shield, AlertTriangle, Calendar, 
  TrendingUp, Users, Euro, Clock, CheckCircle2, AlertCircle,
  FolderOpen, ScrollText, Gavel, Loader2, ArrowRight, BookOpen, Bell, Zap, Bot, Upload,
  Download, ExternalLink, RefreshCw, Target
} from "lucide-react";

// Import sub-pages
import ProfilSociete from "./ProfilSociete";
import CoffreFortIntelligent from "./CoffreFortIntelligent";
import RegistresLegaux from "./RegistresLegaux";
import GenerateurAGO from "./GenerateurAGO";
import GenerateurAGE from "./GenerateurAGE";
import AlertesSettings from "./AlertesSettings";
import ChatbotJuridiqueEnhanced from "./ChatbotJuridiqueEnhanced";
import ImportDocuments from "./ImportDocuments";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DroitSocietesHub() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [exerciceSummary, setExerciceSummary] = useState(null);
  const [conformityScore, setConformityScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  }, [token]);

  // Fetch exercice summary
  const fetchExerciceSummary = useCallback(async () => {
    try {
      const currentYear = new Date().getFullYear();
      const res = await axios.get(`${API}/api/corporate/exercice/${currentYear - 1}/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExerciceSummary(res.data);
    } catch (error) {
      console.error("Error fetching exercice summary:", error);
    }
  }, [token]);

  // Fetch conformity score
  const fetchConformityScore = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/conformity-score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConformityScore(res.data);
    } catch (error) {
      console.error("Error fetching conformity score:", error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchExerciceSummary(), fetchConformityScore()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchExerciceSummary, fetchConformityScore]);

  // Refresh dashboard when tab changes (to sync profile status)
  useEffect(() => {
    if (activeTab) {
      fetchDashboard();
      fetchConformityScore();
    }
  }, [activeTab, fetchDashboard, fetchConformityScore]);

  // Export PDF dossier juridique complet
  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await axios.get(`${API}/api/corporate/export-dossier-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dossier_juridique_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Dossier juridique exporté avec succès");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Erreur lors de l'export du dossier");
    } finally {
      setExportingPdf(false);
    }
  };

  // Open Infogreffe
  const handleOpenInfogreffe = () => {
    window.open("https://www.infogreffe.fr/", "_blank");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "haute": return "bg-red-100 text-red-700 border-red-200";
      case "moyenne": return "bg-orange-100 text-orange-700 border-orange-200";
      default: return "bg-blue-100 text-blue-700 border-blue-200";
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "haute": return <AlertTriangle className="w-4 h-4" />;
      case "moyenne": return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <Gavel className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Droit des Sociétés</h1>
                <p className="text-sm text-gray-500">Gestion juridique et documents légaux</p>
              </div>
            </div>
            
            {/* Quick status badges and actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Score de conformité mini */}
              {conformityScore && (
                <Badge 
                  className={`gap-1 cursor-pointer ${
                    conformityScore.score >= 80 ? 'bg-green-100 text-green-700' :
                    conformityScore.score >= 50 ? 'bg-orange-100 text-orange-700' :
                    'bg-red-100 text-red-700'
                  }`}
                  onClick={() => setActiveTab("chatbot")}
                >
                  <Target className="w-3 h-3" />
                  {conformityScore.score}%
                </Badge>
              )}
              
              {dashboard?.profile_complete ? (
                <Badge className="bg-green-100 text-green-700 gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Profil complet
                </Badge>
              ) : (
                <Badge variant="outline" className="text-orange-600 border-orange-300 gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Profil incomplet
                </Badge>
              )}
              
              {dashboard?.stats?.ago_effectuee && (
                <Badge className="bg-green-100 text-green-700 gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  AGO {new Date().getFullYear() - 1}
                </Badge>
              )}
              
              {/* Boutons d'actions rapides */}
              <div className="flex items-center gap-1 ml-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                  className="h-7 text-xs"
                >
                  {exportingPdf ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3" />
                  )}
                  <span className="hidden sm:inline ml-1">Export PDF</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleOpenInfogreffe}
                  className="h-7 text-xs"
                >
                  <ExternalLink className="w-3 h-3" />
                  <span className="hidden sm:inline ml-1">Infogreffe</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 sm:grid-cols-9 lg:w-auto lg:inline-grid h-auto p-1 bg-white border shadow-sm">
            <TabsTrigger 
              value="dashboard" 
              className="gap-2 py-3 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"
            >
              <TrendingUp className="w-4 h-4" />
              <span className="hidden lg:inline">Accueil</span>
            </TabsTrigger>
            <TabsTrigger 
              value="import" 
              className="gap-2 py-3 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700"
              data-testid="import-tab"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden lg:inline">Import</span>
            </TabsTrigger>
            <TabsTrigger 
              value="profil" 
              className="gap-2 py-3 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"
            >
              <Building2 className="w-4 h-4" />
              <span className="hidden lg:inline">Profil</span>
            </TabsTrigger>
            <TabsTrigger 
              value="registres" 
              className="gap-2 py-3 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden lg:inline">Registres</span>
            </TabsTrigger>
            <TabsTrigger 
              value="ago" 
              className="gap-2 py-3 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"
            >
              <Gavel className="w-4 h-4" />
              <span className="hidden lg:inline">AGO</span>
            </TabsTrigger>
            <TabsTrigger 
              value="age" 
              className="gap-2 py-3 data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700"
            >
              <Zap className="w-4 h-4" />
              <span className="hidden lg:inline">AGE</span>
            </TabsTrigger>
            <TabsTrigger 
              value="chatbot" 
              className="gap-2 py-3 data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700"
              data-testid="chatbot-tab"
            >
              <Bot className="w-4 h-4" />
              <span className="hidden lg:inline">IA</span>
            </TabsTrigger>
            <TabsTrigger 
              value="alertes" 
              className="gap-2 py-3 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"
            >
              <Bell className="w-4 h-4" />
              <span className="hidden lg:inline">Alertes</span>
            </TabsTrigger>
            <TabsTrigger 
              value="coffre" 
              className="gap-2 py-3 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"
            >
              <Shield className="w-4 h-4" />
              <span className="hidden lg:inline">Coffre</span>
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <>
                {/* Alerts Section */}
                {dashboard?.alerts && dashboard.alerts.length > 0 && (
                  <div className="space-y-3">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      Alertes et échéances
                    </h2>
                    <div className="grid gap-3">
                      {dashboard.alerts.map((alert) => (
                        <Card 
                          key={alert.id} 
                          className={`border-l-4 ${getPriorityColor(alert.priorite)}`}
                          data-testid={`alert-${alert.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg ${getPriorityColor(alert.priorite)}`}>
                                  {getPriorityIcon(alert.priorite)}
                                </div>
                                <div>
                                  <h3 className="font-medium text-gray-900">{alert.titre}</h3>
                                  <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                                  {alert.date_echeance && (
                                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      Échéance : {new Date(alert.date_echeance).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                                      {alert.jours_restants !== null && (
                                        <span className="font-medium ml-2">
                                          ({alert.jours_restants} jours restants)
                                        </span>
                                      )}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {alert.type === "profile" && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => setActiveTab("profil")}
                                  className="shrink-0"
                                >
                                  Compléter
                                  <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                              )}
                              
                              {alert.type === "ago_deadline" && (
                                <Button 
                                  size="sm"
                                  className="shrink-0 bg-indigo-600 hover:bg-indigo-700"
                                  disabled
                                >
                                  Générer AGO
                                  <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Score de Conformité - Visible sur l'Accueil */}
                {conformityScore && (
                  <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-3 rounded-xl ${
                            conformityScore.score >= 80 ? 'bg-green-100' :
                            conformityScore.score >= 50 ? 'bg-orange-100' : 'bg-red-100'
                          }`}>
                            <Target className={`w-6 h-6 ${
                              conformityScore.score >= 80 ? 'text-green-600' :
                              conformityScore.score >= 50 ? 'text-orange-600' : 'text-red-600'
                            }`} />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 font-medium">Score de conformité</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className={`text-3xl font-bold ${
                                conformityScore.score >= 80 ? 'text-green-600' :
                                conformityScore.score >= 50 ? 'text-orange-600' : 'text-red-600'
                              }`}>
                                {conformityScore.score}%
                              </span>
                              <Progress 
                                value={conformityScore.score} 
                                className="w-32 h-2"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {conformityScore.items_to_complete > 0 && (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              {conformityScore.items_to_complete} points à compléter
                            </Badge>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="mt-2"
                            onClick={() => setActiveTab("chatbot")}
                          >
                            Voir détails
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                      </div>
                      {conformityScore.priorities && conformityScore.priorities.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-indigo-200">
                          <p className="text-xs text-gray-500 mb-2">Priorités :</p>
                          <div className="flex flex-wrap gap-2">
                            {conformityScore.priorities.slice(0, 3).map((priority, idx) => (
                              <Badge 
                                key={idx} 
                                variant="outline" 
                                className={`text-xs ${
                                  priority.urgency === 'haute' ? 'border-red-300 text-red-600' :
                                  priority.urgency === 'moyenne' ? 'border-orange-300 text-orange-600' :
                                  'border-blue-300 text-blue-600'
                                }`}
                              >
                                {priority.label}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Stats Cards - Cliquables */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-blue-300"
                    onClick={() => setActiveTab("coffre")}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {dashboard?.stats?.documents_count || 0}
                          </p>
                          <p className="text-sm text-gray-500">Documents archivés</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-purple-300"
                    onClick={() => setActiveTab("registres")}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <ScrollText className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {dashboard?.stats?.registres_a_jour || 0}/{dashboard?.stats?.registres_total || 7}
                          </p>
                          <p className="text-sm text-gray-500">Registres à jour</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-green-300"
                    onClick={() => setActiveTab("ago")}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          dashboard?.stats?.ago_effectuee ? 'bg-green-100' : 'bg-orange-100'
                        }`}>
                          <Users className={`w-5 h-5 ${
                            dashboard?.stats?.ago_effectuee ? 'text-green-600' : 'text-orange-600'
                          }`} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {dashboard?.stats?.ago_effectuee ? "Oui" : "Non"}
                          </p>
                          <p className="text-sm text-gray-500">AGO {new Date().getFullYear() - 1}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-indigo-300"
                    onClick={() => {
                      if (!dashboard?.stats?.comptes_deposes) {
                        // Ouvrir Infogreffe pour déposer les comptes
                        window.open("https://www.infogreffe.fr/formalites/depot-des-comptes", "_blank");
                      }
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          dashboard?.stats?.comptes_deposes ? 'bg-green-100' : 'bg-orange-100'
                        }`}>
                          <CheckCircle2 className={`w-5 h-5 ${
                            dashboard?.stats?.comptes_deposes ? 'text-green-600' : 'text-orange-600'
                          }`} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {dashboard?.stats?.comptes_deposes ? "Oui" : "Non"}
                          </p>
                          <p className="text-sm text-gray-500">
                            Comptes déposés
                            {!dashboard?.stats?.comptes_deposes && (
                              <span className="block text-xs text-orange-500">Cliquez pour déposer →</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Exercice Summary - Now based on imported bilans */}
                {exerciceSummary && exerciceSummary.source === "bilan_importe" ? (
                  <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        Exercice {exerciceSummary.exercice}
                        <Badge className="ml-2 bg-green-500">Bilan importé</Badge>
                      </CardTitle>
                      <CardDescription className="text-green-700">
                        Données issues du bilan importé
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="p-4 bg-white/60 rounded-lg border border-green-200">
                          <p className="text-sm text-green-700 font-medium">Chiffre d'affaires</p>
                          <p className="text-2xl font-bold text-green-900 mt-1">
                            {formatCurrency(exerciceSummary.chiffre_affaires)}
                          </p>
                        </div>
                        
                        <div className="p-4 bg-white/60 rounded-lg border border-green-200">
                          <p className="text-sm text-green-700 font-medium">Résultat net</p>
                          <p className={`text-2xl font-bold mt-1 ${
                            exerciceSummary.resultat_net >= 0 ? 'text-green-900' : 'text-red-600'
                          }`}>
                            {formatCurrency(exerciceSummary.resultat_net)}
                          </p>
                        </div>
                        
                        <div className="p-4 bg-white/60 rounded-lg border border-green-200">
                          <p className="text-sm text-green-700 font-medium">Capitaux propres</p>
                          <p className="text-2xl font-bold text-green-900 mt-1">
                            {formatCurrency(exerciceSummary.capitaux_propres)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        Données financières en attente
                      </CardTitle>
                      <CardDescription className="text-amber-700">
                        Importez votre bilan pour afficher les données comptables officielles
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between p-4 bg-white/60 rounded-lg border border-amber-200">
                        <div>
                          <p className="font-medium text-amber-900">Aucun bilan importé</p>
                          <p className="text-sm text-amber-700 mt-1">
                            Les données financières seront extraites automatiquement de votre liasse fiscale
                          </p>
                        </div>
                        <Button 
                          onClick={() => setActiveTab("import")}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          Importer un bilan
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Import Card - Featured */}
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50"
                    onClick={() => setActiveTab("import")}
                    data-testid="import-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
                          <Upload className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Import Intelligent</h3>
                          <p className="text-sm text-gray-500">OCR automatique de vos documents</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-indigo-200"
                    onClick={() => setActiveTab("profil")}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-indigo-100 rounded-xl">
                          <Building2 className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Profil Société</h3>
                          <p className="text-sm text-gray-500">Informations légales, capital, associés</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-indigo-200"
                    onClick={() => setActiveTab("coffre")}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-100 rounded-xl">
                          <Shield className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Coffre-Fort</h3>
                          <p className="text-sm text-gray-500">Documents juridiques archivés</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-orange-200"
                    onClick={() => setActiveTab("age")}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-orange-100 rounded-xl">
                          <Zap className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Générer AGE</h3>
                          <p className="text-sm text-gray-500">Événements extraordinaires</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50"
                    onClick={() => setActiveTab("chatbot")}
                    data-testid="chatbot-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
                          <Bot className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Assistant IA</h3>
                          <p className="text-sm text-gray-500">Chatbot juridique intelligent</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Import Intelligent Tab */}
          <TabsContent value="import">
            <ImportDocuments />
          </TabsContent>

          {/* Profil Société Tab */}
          <TabsContent value="profil">
            <ProfilSociete />
          </TabsContent>

          {/* Registres Légaux Tab */}
          <TabsContent value="registres">
            <RegistresLegaux />
          </TabsContent>

          {/* AGO Generation Tab */}
          <TabsContent value="ago">
            <GenerateurAGO />
          </TabsContent>

          {/* AGE Generation Tab */}
          <TabsContent value="age">
            <GenerateurAGE />
          </TabsContent>

          {/* Chatbot Juridique Tab */}
          <TabsContent value="chatbot">
            <ChatbotJuridiqueEnhanced onNavigate={(tab) => setActiveTab(tab)} />
          </TabsContent>

          {/* Alertes Tab */}
          <TabsContent value="alertes">
            <AlertesSettings />
          </TabsContent>

          {/* Coffre-Fort Tab */}
          <TabsContent value="coffre">
            <CoffreFortIntelligent onNavigate={(tab) => setActiveTab(tab)} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
